import React, { useState } from "react";
import EntityDropdown from "../../../component/EntityDropdown";
import { POINTS_ADMIN } from "../../../config/lang";
import { translate } from "../../../util/util";
import {
  ANSWER_TYPES, ANSWER_TYPE_LABELS,
  PRIORITIES, PRIORITY_LABELS,
  QUESTION_MODES, QUESTION_MODE_LABELS,
  PRODUCTS_SOURCES, PRODUCTS_SOURCE_LABELS,
  TARGETING_GROUPS,
} from "./questionOptions";

const emptyQuestion = () => ({
  text: "", answer_type: "text", required: false, priority: 0,
  question_mode: null, products_source: null,
  all_products: false, all_brands: false, all_categories: false, all_subcategories: false,
  products: [], brands: [], categories: [], subcategories: [],
  dropdown_options: [],
});

const emptyOption = () => ({ label: "", value: "" });

// EntityDropdown reports selections as option OBJECTS (so labels keep rendering); this repeater
// isn't covered by useResource.js's top-level "entity" field normalization (it only inspects
// descriptor.fields, not values nested inside a "questions" array), so ids are normalized here.
const toIds = list => (list || []).map(x => (x && typeof x === "object" ? (x.id ?? x.value ?? x) : x));

const withOrder = list => list.map((row, i) => ({ ...row, order: i }));

export default function QuestionsField({ field, value, onChange, lang }) {
  const [questions, setQuestions] = useState(() => value || []);
  const [expandedTargeting, setExpandedTargeting] = useState({});
  const t = key => translate(POINTS_ADMIN, key, lang);

  const sync = next => {
    setQuestions(next);
    onChange(field.name, withOrder(next));
  };

  const updateQuestion = (qi, patch) => sync(questions.map((q, idx) => (idx === qi ? { ...q, ...patch } : q)));
  const addQuestion = () => sync([...questions, emptyQuestion()]);
  const removeQuestion = qi => sync(questions.filter((_, idx) => idx !== qi));

  const updateOptions = (qi, options) => updateQuestion(qi, { dropdown_options: withOrder(options) });
  const addOption = qi => updateOptions(qi, [...(questions[qi].dropdown_options || []), emptyOption()]);
  const removeOption = (qi, oi) => updateOptions(qi, questions[qi].dropdown_options.filter((_, idx) => idx !== oi));
  const updateOption = (qi, oi, patch) =>
    updateOptions(qi, questions[qi].dropdown_options.map((o, idx) => (idx === oi ? { ...o, ...patch } : o)));

  const toggleTargeting = qi => setExpandedTargeting(s => ({ ...s, [qi]: !s[qi] }));

  return (
    <div className="pt-questions">
      {!questions.length && <div className="pt-questions-empty">{t("no_questions")}</div>}
      <div className="pt-question-list">
        {questions.map((q, qi) => (
          <div className="pt-question-card" key={qi}>
            <div className="pt-question-card-header">
              <span className="pt-question-index">{qi + 1}</span>
              <input
                className="mp-form-input pt-question-text"
                placeholder={t("f_question_text")}
                value={q.text}
                onChange={e => updateQuestion(qi, { text: e.target.value })}
              />
              <button type="button" className="pt-kv-remove" aria-label="remove" onClick={() => removeQuestion(qi)} title={t("question_remove_btn")}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="pt-question-grid">
              <div className="mp-form-group">
                <label className="mp-form-label">{t("f_answer_type")}</label>
                <select className="mp-form-input" value={q.answer_type} onChange={e => updateQuestion(qi, { answer_type: e.target.value })}>
                  {ANSWER_TYPES.map(v => <option key={v} value={v}>{t(ANSWER_TYPE_LABELS[v])}</option>)}
                </select>
              </div>
              <div className="mp-form-group">
                <label className="mp-form-label">{t("f_priority")}</label>
                <select className="mp-form-input" value={q.priority} onChange={e => updateQuestion(qi, { priority: Number(e.target.value) })}>
                  {PRIORITIES.map(v => <option key={v} value={v}>{t(PRIORITY_LABELS[v])}</option>)}
                </select>
              </div>
              <div className="mp-form-group pt-checkbox">
                <label>
                  <input type="checkbox" checked={!!q.required} onChange={e => updateQuestion(qi, { required: e.target.checked })} />
                  <span>{t("f_required")}</span>
                </label>
              </div>
            </div>

            {q.answer_type === "dropdown" && (
              <div className="pt-question-subsection">
                <h4 className="pt-question-subsection-title">{t("f_dropdown_options")}</h4>
                <div className="pt-kv">
                  {(q.dropdown_options || []).map((opt, oi) => (
                    <div className="pt-kv-row" key={oi}>
                      <input
                        className="mp-form-input"
                        placeholder={t("option_label_ph")}
                        value={opt.label}
                        onChange={e => updateOption(qi, oi, { label: e.target.value })}
                      />
                      <input
                        className="mp-form-input"
                        placeholder={t("option_value_ph")}
                        value={opt.value}
                        onChange={e => updateOption(qi, oi, { value: e.target.value })}
                      />
                      <button type="button" className="pt-kv-remove" aria-label="remove" onClick={() => removeOption(qi, oi)}>
                        <span className="material-symbols-outlined">close</span>
                      </button>
                    </div>
                  ))}
                  <button type="button" className="pt-kv-add" onClick={() => addOption(qi)}>
                    <span className="material-symbols-outlined">add</span>
                    {t("option_add_btn")}
                  </button>
                </div>
              </div>
            )}

            <div className="pt-question-subsection">
              <button type="button" className="pt-question-subsection-toggle" onClick={() => toggleTargeting(qi)}>
                <span className="material-symbols-outlined">{expandedTargeting[qi] ? "expand_less" : "expand_more"}</span>
                {t("sec_targeting")}
              </button>
              {expandedTargeting[qi] && (
                <>
                  <div className="pt-question-grid">
                    <div className="mp-form-group">
                      <label className="mp-form-label">{t("f_question_mode")}</label>
                      <select
                        className="mp-form-input"
                        value={q.question_mode || ""}
                        onChange={e => updateQuestion(qi, { question_mode: e.target.value || null })}
                      >
                        <option value="">—</option>
                        {QUESTION_MODES.map(v => <option key={v} value={v}>{t(QUESTION_MODE_LABELS[v])}</option>)}
                      </select>
                    </div>
                    <div className="mp-form-group">
                      <label className="mp-form-label">{t("f_products_source")}</label>
                      <select
                        className="mp-form-input"
                        value={q.products_source || ""}
                        onChange={e => updateQuestion(qi, { products_source: e.target.value || null })}
                      >
                        <option value="">—</option>
                        {PRODUCTS_SOURCES.map(v => <option key={v} value={v}>{t(PRODUCTS_SOURCE_LABELS[v])}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="pt-question-targeting">
                    {TARGETING_GROUPS.map(grp => (
                      <div className="pt-question-targeting-row" key={grp.key}>
                        <label className="pt-checkbox pt-all-toggle">
                          <input
                            type="checkbox"
                            checked={!!q[grp.allKey]}
                            onChange={e => updateQuestion(qi, {
                              [grp.allKey]: e.target.checked,
                              ...(e.target.checked ? { [grp.key]: [] } : {}),
                            })}
                          />
                          <span>{t(grp.allLabelKey)}</span>
                        </label>
                        {!q[grp.allKey] && (
                          <div className="mp-form-group pt-question-entity">
                            <label className="mp-form-label">{t(grp.labelKey)}</label>
                            <EntityDropdown
                              entity={grp.entity}
                              value={q[grp.key] || []}
                              isMulti
                              onChange={o => updateQuestion(qi, { [grp.key]: toIds(o) })}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      <button type="button" className="pt-kv-add pt-question-add" onClick={addQuestion}>
        <span className="material-symbols-outlined">add</span>
        {t("question_add_btn")}
      </button>
    </div>
  );
}
